import * as React from 'react';
import { IDENTITY_TYPE } from 'common/constants/Types';
import i18n from 'common/locales';
import JSRuntime from 'common/utils/JSRuntime';

/** 惰性翻译 Map：.get() 时自动调用 i18n.t()，.has() 保持原行为 */
function createI18nMap<K extends string | number>(
  entries: [K, string][],
): Pick<ReadonlyMap<K, string>, 'has' | 'get'> {
  const raw = new Map(entries);
  return {
    has: (key: K) => raw.has(key),
    get: (key: K) => {
      const val = raw.get(key);
      if (val === undefined) return undefined;
      return val === '' ? '' : i18n.t(val);
    },
  };
}

/** 前缀 */
export const PREFIX = 'oneid';

/** 企业用户不存在 */
export const NOT_FOUND_ERROR = 'E0010006';

/** 登录过期 || 未登录 */
export const NO_AUTH = 'E0010001';

/** 错误码命中G40301时，没有相关权限，需要特殊报错或特殊逻辑 */
export const NO_PERMISSION = 'G40301';

/** 监控报警忽略的错误消息 */
export const AEGIS_IGNORE_MESSAGES = [
  'Script error. @ (:0:0)',
  'Error.stack: undefined',
  'at <anonymous>',
  'Error.stack: global code',
  'chrome-extension://',
];

/** 正则匹配监控报警忽略的错误消息，注意性能问题，可能会堵塞页面渲染 */
export const AEGIS_IGNORE_REGEXPS = [/Error\.stack:\s*$/];

export enum SAVE_ORG_MSG_CODE {
  EXIST = 'E0010039',
  NOT_COMPLIANCE = 'E0010132', // 不合规
}

// 手动取消滑块或者滑块发生错误，不进行报错提示
export const CAPTCHA_CANCEL = 'CAPTCHA_CANCEL';

// 滑块内部错误
export const CAPTCHA_ERROR = 'CAPTCHA_ERROR';

// 获取密码加密公钥接口错误
export const CREDENTIAL_ERROR = 'CREDENTIAL_ERROR';

// 手机号触发安全风险，禁止使用
export const MOBILE_SAFE_ERR_CODE = 'E0010334';

// 邮箱白名单用户在登录时拦截
export const EMAIL_WHITELIST_ERR = {
  code: 'E0010364',
  get msg() {
    return i18n.t('该企业不允许邮箱登录');
  },
};
/** 安全合规-内容风险 专用错误码 */
export const INVALID_CONTENT_ERROR_CODE = 'E0010217';

/** 统一错误码报错文案（.get() 自动返回翻译后的文案） */
export const ERROR_MSG = createI18nMap<string | number>([
  [401, '访问未授权'],
  [403, '访问无权限'],
  [404, '无法访问'],
  [405, '操作无效'],
  [408, '连接超时'],
  [500, '服务器异常'],
  [502, '服务器无响应'],
  [503, '站点正在维护'],
  [504, '服务器间连接异常'],
  [0, '网络未连接'],
  ['E0000051', ''], // 搜索超过64字符
  ['E0010113', '当前链接已失效！'], // 应用开通绑定租户时code无效
  [INVALID_CONTENT_ERROR_CODE, '内容保存失败，请求修改后重试'], // 安全合规-内容风险 报错兜底（出现此文案则需进行排查）
  [MOBILE_SAFE_ERR_CODE, '检测到安全风险，操作已被拦截'],
  [CAPTCHA_ERROR, ''],
  [CAPTCHA_CANCEL, ''],
  [CREDENTIAL_ERROR, '服务器出现了问题，请再试一次'],
]);

export const NO_COMPANY_ERROR = 'NO_COMPANY_ERROR'; // 后端没有具体验证码，前端伪造一个用来展示错误

// 发送验证码达到上限，需要特殊处理的报错（.get() 自动返回翻译后的文案）
export const VERIFY_CODE_LIMIT_ERR = createI18nMap<string>([
  ['E0010161', '今日验证码发送次数已达上限'],
  ['E0010229', '请求过于频繁，请稍后再试'],
]);

// 手机号是网状用户
export const NET_PHONE_ERROR_CODE = 'E0010332';

/** 全局enum */
export enum IDP_TYPE {
  WeCom = 'WeCom',
}

export enum WECOM_BIND_STATUS {
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
}

// 企业用户名登录状态
export enum USERNAME_LOGIN_STATUS {
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
}

/** 租户类型 */
export enum ACCOUNT_TYPE {
  /** 企业版 */
  COMPANY = 1,
  /** 个人版 */
  PERSONAL = 2,
}

/** 租户类型文案 */
export const ACCOUNT_TYPE_TEXT: Record<number, string> = {
  get [ACCOUNT_TYPE.COMPANY]() {
    return i18n.t('企业版');
  },
  get [ACCOUNT_TYPE.PERSONAL]() {
    return i18n.t('个人版');
  },
};

/** 登录方式 */
export enum AUTHENTICATION_METHOD {
  /** 企业账号登录 */
  ENTERPRISE = 'Enterprise',
  /** 自然人账号登录 */
  PERSON = 'Person',
  /** 邮箱账号登录 */
  EMAIL = 'Email',
}

// 应用服务商类型
export enum VENDOR_TYPE {
  SSO = 'SSO', // 应用商城应用
  Tencent = 'Tencent', // 腾讯一方应用
  External = 'External', // 第三方应用/服务商应用
  Internal = 'Internal', // 企业自建应用
  KIT = 'KIT', // 腾讯套件应用
}

export const APP_STORE_SIGN_ON_MODE = {
  OIDC: 'OIDC',
};

/** 全局错误兜底文案（已翻译，直接用于展示） */
export const getUnknownMistake = () => i18n.t('未知错误，请稍后重试');

export enum APP_EDITION_TYPE {
  COMPANY = 1, // 企业版应用
  PERSONAL = 2, // 个人版应用
}

export const DEFAULT_COUNTDOWN_SECOND = 59;

export enum USER_VERIFY_CODE_SCENE {
  UPDATE_MOBILE = 'update_mobile',
  VERIFY_BEFORE_UPDATE_MOBILE = 'verify_before_update_mobile',
  ASSIGN_USER = 'assign_user',
  ACCOUNT_CANCELLATION = 'account_cancellation',
  RESET_PASSWORD = 'reset_password',
  RESET_ENTERPRISE_PASSWORD = 'reset_enterprise_password',
  TENANT_CHANGE_BIND = 'tenant_change_bind', // 租户换绑
  /** 个人退出 */
  LOGOFF_USER = 'logoff_user',
  /** 解散企业 */
  ACCOUNT_DISMISS = 'account_dismiss',
}

export enum EMAIL_VERIFY_CODE_SCENE {
  EMAIL_LOGIN = 'email_login', // 邮箱登录
  EMAIL_SET = 'email_set', // 设置邮箱
  PASSWORD_SET = 'password_set', // 设置密码
  PASSWORD_CHANGE = 'password_change', // 修改密码
  EMAIL_REBIND = 'email_rebind', // 换绑 - 旧邮箱
  EMAIL_REBIND_NEW = 'email_rebind_new', // 换绑 - 旧邮箱
  EMAIL_UNBIND = 'email_unbind', // 解绑
}

export enum COMMON_VERIFY_CODE_SCENE {
  TRANSFER_CREATOR = 'transfer_creator',
}

/** 防抖默认时间 */
export const DEBOUNCE_DEFAULT_SECOND = 300;
/** 节流默认时间 */
export const THROTTLE_DEFAULT_SECOND = 1000;
/** 链接跳转时，重复会被浏览器cancel，需要节流 */
export const THROTTLE_REDIRECT_SECOND = 3000;

// 支持企业级登录页外观自定义-登录框位置
export enum LOGIN_POSITIONS {
  DEFAULT = 'DEFAULT',
  LEFT = 'LEFT',
  CENTER = 'CENTER',
  RIGHT = 'RIGHT',
}

// 支持企业级登录页外观自定义-登录框位置
export const POSITIONS = {
  [LOGIN_POSITIONS.LEFT]: 'left',
  [LOGIN_POSITIONS.CENTER]: 'center',
  [LOGIN_POSITIONS.RIGHT]: 'right',
};

// 支持企业级登录页外观自定义-背景类型
export enum BACKGROUND_TYPES {
  IMAGE = 'IMAGE',
  COLOR = 'COLOR',
}

// 支持企业级登录页外观自定义-背景填充类型
export enum FILLING_TYPES {
  COVER = 'COVER', // 自适应屏幕 background-size: cover
  CONTAIN = 'CONTAIN', // 任意边充满屏幕 background-size: contain
}

// 企业登录页外观自定义模版类型：SaaS、meeting、doc
export enum TEMPLATES {
  SAAS = 'SaaS', // 常规登录页
  MEETING = 'meeting', // 腾会登录页
  DOC = 'doc', // 文档登录页
}

/* 密码策略相关常量 */

// 字符中必须包含
export enum INCLUDE {
  MIN_LOWER_CASE = 'minLowerCase', // 小写字母
  MIN_UPPER_CASE = 'minUpperCase', // 大写字母
  MIN_NUMBER = 'minNumber', // 数字字符0-9
  MIN_SYMBOL = 'minSymbol', // 特殊字符（例如*_.$!等）
}

// 字符中不可包含
export enum EXCLUDE {
  USERNAME = 'username', // 登录用户名
  PHONE_NUM = 'phoneNum', // 手机号码
  EMAIL_PREFIX = 'emailPrefix', // 邮箱前缀
  CARD_NUM = 'cardNum', // 工号
}

// 密码过期时间
export enum EXPIRATION_TIME {
  UNLIMITED = '0', // 不限
  FIFTEEN = '15', // 15天后过期
  THIRTY = '30', // 30天后过期
  FIFTY_FIVE = '45', // 45天后过期
  SIXTY = '60', // 60天后过期
  NINETY = '90', // 90天后过期
}

// 密码重复规则
export enum REPEAT_RULE {
  UNLIMITED = '0', // 不限
  ONE = '1', // 不可与前1个密码重复
  TWO = '2', // 不可与前2个密码重复
  THREE = '3', // 不可与前3个密码重复
}

export enum VERIFY_CODE_SCENE {
  LOGIN = 'login',
  FORGET_PASSWORD = 'forget_password',
  SOCIAL_LOGIN = 'social_login',
  INVITATION = 'invitation',
  BIND_MOBILE = 'bind_mobile', // 微信绑定手机号
  COMPLETE_MOBILE = 'complete_mobile', // 补全账户缺失的手机号
  EMAIL_HANDSHAKE = 'email_handshake', // 邮箱接受邀请
  NECESSARY_MOBILE = 'necessary_mobile', // 个人账号绑定手机号
}

const SAAS_TEMPLATE = {
  position: LOGIN_POSITIONS.DEFAULT,
  backgroundType: BACKGROUND_TYPES.COLOR,
  imageUrl: '',
  fillingType: FILLING_TYPES.COVER,
  color: '#e6f1ff',
  customCssEnabled: false,
  css: '',
  template: TEMPLATES.SAAS,
  id: '',
};

const DOC_TEMPLATE = {
  position: LOGIN_POSITIONS.DEFAULT,
  backgroundType: BACKGROUND_TYPES.IMAGE,
  imageUrl: JSRuntime.getStaticPath('assets/docsLoginBg.png'),
  fillingType: FILLING_TYPES.COVER,
  color: '#ffffff',
  customCssEnabled: false,
  css: '',
  template: TEMPLATES.DOC,
  id: '',
};

export const TEMPLATE_MAP = {
  [TEMPLATES.SAAS]: SAAS_TEMPLATE,
  [TEMPLATES.DOC]: DOC_TEMPLATE,
};

export enum AUTH_UPLOAD_TYPE {
  /** 个人认证头像 */
  PERSONAL_AVATAR = 'personal_avatar',
  /** 个人认证图片 */
  PERSONAL_CERTIFY = 'personal_certify',
  /** 企业认证logo */
  ACCOUNT_LOGO = 'account_logo',
  /** 企业认证图片 */
  ACCOUNT_CERTIFY = 'account_certify',
}

// 新上传图片流程中获取token和获取临时链接需要的upload_resource，全局唯一
export const UPLOAD_IMG_KEY = new Set([
  'avatar',
  'job_statements',
  'other_materials',
  'external_account_ownership_statements',
  'profession_qualifications',
  'additional_materials',
  'get_eid_corpmgr_corp_logo', // 企业logo
  'get_eid_corpmgr_auth_logo', // 认证源logo
  'get_eid_corpmgr_data_logo', // 数据源logo
  'get_eid_corpmgr_system_background', // 自定义背景
  'account_sync_data_logo', // 新版数据源logo
  'account_workspace_logo', // 新版工作台logo
  'account_idp_logo', // 新版认证源logo
  'account_user_logo', // 个人头像
  'account_app_logo', // 自建应用
  AUTH_UPLOAD_TYPE.PERSONAL_AVATAR,
  AUTH_UPLOAD_TYPE.PERSONAL_CERTIFY,
  AUTH_UPLOAD_TYPE.ACCOUNT_LOGO,
  AUTH_UPLOAD_TYPE.ACCOUNT_CERTIFY,
]);

// 图片审核结果
export enum IMG_SAFE_CHECK_STATUS {
  BEFORE_CHECK = 0, // 未送审
  CHECK = 1, // 审核中
  SUCCESS = 2, // 审核成功
  FAIL = 3, // 审核失败
}

export enum IMG_SAFE_CHECK_TYPE {
  PERSON = 0,
  IMG_AUDITING = 1,
}

/** localStorage的key */
export enum LOCAL_STORAGE {
  /** 脱敏信息展示的二次提示，存储上次勾选不再提示的时间（2025-01-01） */
  REALITY_INFO_TIP_DATE = 'REALITY_INFO_TIP_DATE',
  /** 记录SSO登录时，用户选择的认证方式的偏好 */
  SSO_LOGIN_IDENTITY_ID = 'SSO_LOGIN_IDENTITY_ID',
  /** COS COOKIE的信息，包含过期时间和登录用户的信息
   * expireTime: string
   * unionId: string
   */
  COS_COOKIE_INFO = 'COS_COOKIE_INFO',
}

/** 企业配置/开关状态 */
export enum SWITCH_OPTION {
  SWITCH_ON = 'SwitchOn',
  SWITCH_OFF = 'SwitchOff',
}

/** 版本拥有的功能点
 * @description 独立的版本点：需要在页面ui处针对版本信息中进行一次判断或单独请求接口；
 * @description 菜单相关的版本点：会根据版本信息进行过滤，去除当前版本不允许的菜单；
 * @description 权限相关的版本点：会先根据版本信息进行筛查，再根据按钮权限进行筛查。
 */
export enum VERSION_POINT {
  /** 企业解散
   * @description 独立
   */
  ACCOUNT_DISSOLUTION = 1,
  /** 人员退出
   * @description 独立
   */
  PERSON_QUIT = 2,
  /** 首页菜单
   * @description 菜单相关
   */
  MENU_GROUP = 3,
  /** 组织架构权限
   * @description 权限相关
   */
  ORGANIZATION = 4,
  /** 可见范围
   * @description 菜单相关
   */
  SCOPE = 5,
  /** 办公套件
   * @description 菜单相关
   */
  KIT = 6,
  /** 管理员角色
   * @description 菜单相关
   */
  ROLE = 7,
  /** agent管理
   * @description 菜单相关
   */
  AGENTS = 8,
  /** 自建应用 */
  SELF_APP = 9,
  /** SSO认证源
   * @description 菜单相关，与account中ssoSwitch相同
   */
  SSO = 10,
  /** 批量操作
   * @description 按钮权限相关
   */
  BATCH_OPERATE = 11,
  /** 激活邀请
   * @description 权限相关
   */
  ACTIVE_INVITE = 12,
  /** 企业信息
   * @description 菜单相关
   */
  ACCOUNT = 13,
  /** LDAP源 AD源
   * @description 该版本点相对独立，不受其他权限、ui影响
   */
  LDAP = 14,
  /** 查看脱敏后信息
   * @description 与权限中的查看脱敏信息无关，此处用于控制是否可以查看明文信息，与权限中的查看明文手机和邮箱相同
   */
  VIEW_REAL_INFO = 15,
  /** 系统外观
   * @description 菜单相关
   */
  BRANDING = 16,
  /**
   * 用户日志
   * @description 菜单相关
   */
  LOG = 17,
  /** 邮箱相关
   * @description 文案调整
   */
  EMAIL = 18,
  /** 帮助文档
   * @description 菜单相关
   */
  README = 19,
  /** 个人中心 - 右上角下拉菜单
   * @description 菜单相关
   */
  PROFILE = 20,
  /** 个人工作台 - 右上角下拉菜单
   * @description 菜单相关
   */
  PORTAL = 21,
  /** 显示企业 Or 团队相关文案
   * @description 文案调整
   */
  ENTERPRISE_TEXT = 22,
  /** 显示企业 Or 团队相关功能
   * @description 功能调整
   */
  ENTERPRISE_FUNC = 23,
  /**
   * 切换账号功能
   * @description 按钮功能调整
   */
  CHANGE_ACCOUNT = 24,
  /**
   * 邀请与审批
   * @description 功能相关
   */
  INVITE_APPROVAL = 25,
  /**
   * 全局邀请开关
   * @description 功能相关
   */
  GLOBAL_INVITE = 26,
}
/** 认证源/数据源场景下，复用一套创建组件时，不同类型的相关配置 */
export const IDENTITY_CONFIGS = {
  [IDENTITY_TYPE.AD]: {
    name: 'AD',
    get userDesc() {
      return (
        <div>
          {i18n.t(
            '支持使用 UPN 格式 (admin@example.com)、域前置格式 (example\\admin)、DN 格式 (cn=admin, ou=技术部, dc=example, dc=com)',
          )}
        </div>
      );
    },
  },
  [IDENTITY_TYPE.OPENLDAP]: {
    name: 'LDAP',
    get userDesc() {
      return <div>{i18n.t('仅支持使用 DN 格式（CN=AdminUser,OU=Admins,DC=example,DC=com）')}</div>;
    },
  },
};

/** 二次认证方式 */
export enum CertifyMethod {
  FACE = 'face',
  SMS = 'sms',
  CLOSE = 'close',
}

/** 二次认证状态 */
export enum CertifyStatus {
  PASS = 'pass',
  /** 人脸验证，只显示二维码页面 */
  FACE = 'face',
  /** 短信验证，只显示手机验证码页面 */
  SMS = 'sms',
  /** 需要绑定人脸，已绑定手机号*/
  NEED_FACE = 'need_face',
  /** 需要绑定手机号，已绑定人脸 */
  NEED_PHONE = 'need_phone',
  /** 需要绑定手机号和人脸 */
  NEED_BOTH = 'need_both',
}

/** 二次认证状态对应的事件 */
export const CertifyEvent = {
  [CertifyStatus.FACE]: 'admin_auth',
  [CertifyStatus.SMS]: 'admin_auth',
  [CertifyStatus.NEED_PHONE]: 'assign_user_mfa',
  [CertifyStatus.NEED_FACE]: 'verify_only',
  [CertifyStatus.NEED_BOTH]: 'assign_user',
};

/** 新手引导字段名 */
export enum NEW_GUIDE_NAME {
  /** 新手指引 */
  USERS_NEW_GUIDE = 'usersNewGuide',
  /** 管理后台使用，二次认证前往设置弹窗 */
  CERTIFY_NEW_GUIDE = 'certifyNewGuide',
  /** idp、portal使用，安全策略升级新手指引 */
  CERTIFY_SECURITY_UPGRADE_GUIDE = 'certifySecurityUpgradeGuide',
}

/** 部门邀请不存在报错。
 * 管理后台: 创建邀请
 * 被邀请页面: 失效
 */
export const INVITATION_NOT_FOUND = ['E0520003', 'E0520004', 'E0520009', 'E0520010', 'E0200001'];

export { APP_TYPE } from './Types';

/** 默认自建应用头像 */
export const DEFAULT_SELF_APP_LOGO = `${window.ONEID_PUBLIC_CDN_URL}/images/logo/self-app.png`;
